/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { use, type ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { useSite } from '@salesforce/storefront-next-runtime/site-context';
import type { ShopperBasketsV2, ShopperPromotions } from '@/scapi';
import { formatCurrency } from '@/lib/currency';
import { cn } from '@/lib/utils';
import { isRecyclingAdjustmentByLabel } from '@/lib/cart/recycling-fee';

type PriceAdjustment = ShopperBasketsV2.schemas['PriceAdjustment'];

export interface CartLinePromoCalloutsProps {
    /**
     * Item-level price adjustments from the basket productItem.
     * Each entry represents one applied product-level promotion.
     * Sorted by priceAdjustmentId ascending for stable render order.
     */
    priceAdjustments?: PriceAdjustment[];
    /**
     * Promotions map keyed by promotionId. When provided, the promotion's
     * customer-facing offerMsg is used as the callout label in preference
     * to the merchant-authored itemText on the price adjustment.
     */
    promotions?: Record<string, ShopperPromotions.schemas['Promotion']>;
    className?: string;
}

/**
 * Renders one brand-red callout row per item-level price adjustment on a
 * cart line item. Replaces the muted `Saved $X.XX` badge from
 * `<ProductItemPromotions/>` with a per-promo list driven by
 * `productItems[].priceAdjustments[]`.
 *
 * Strikethrough source: `productItem.price × quantity` (owned by caller).
 * Format: `[itemText] (-$X.XX)` via i18n key `cart.promo.lineCallout`.
 * Plain text only — no `dangerouslySetInnerHTML`.
 * Returns null when `priceAdjustments` is empty or undefined.
 */
export default function CartLinePromoCallouts({ priceAdjustments, promotions, className }: CartLinePromoCalloutsProps) {
    const { t, i18n } = useTranslation('cart');
    const { currency } = useSite();

    if (!priceAdjustments?.length) return null;

    // Recycling fees are rendered in Order Summary "Taxes and Fees", not as
    // inline product-level promo callouts. Keep this guard here so any caller
    // path that forgets to pre-filter adjustments still cannot surface it.
    const nonRecyclingAdjustments = priceAdjustments.filter((adj) => !isRecyclingAdjustmentByLabel(adj));
    if (!nonRecyclingAdjustments.length) return null;

    // Stable render order: sort ascending by priceAdjustmentId.
    // SCAPI does not guarantee array order across requests.
    const sorted = [...nonRecyclingAdjustments].sort((a, b) => {
        const idA = a.priceAdjustmentId ?? '';
        const idB = b.priceAdjustmentId ?? '';
        if (idA < idB) return -1;
        if (idA > idB) return 1;
        return 0;
    });

    return (
        <div className={cn('flex flex-col gap-0.5', className)}>
            {sorted.map((adj) => {
                const promoDetails = adj.promotionId ? promotions?.[adj.promotionId] : undefined;
                // Prefer richer customer-facing copy in order:
                // 1. offerMsg from Promotions API (longest, most descriptive)
                // 2. calloutMsg from Promotions API (shorter tagline)
                // 3. calloutMsg on the price adjustment itself (basket-level, no extra fetch)
                // 4. itemText on the adjustment — only when it is NOT the raw coupon code
                //    (SFCC sometimes sets itemText = couponCode; never expose the code to customers)
                const safeItemText = adj.itemText && adj.itemText !== adj.couponCode ? adj.itemText : undefined;
                const label =
                    promoDetails?.offerMsg || promoDetails?.calloutMsg || adj.calloutMsg || safeItemText || '';
                const isDiscount = (adj.price ?? 0) < 0;
                const amount = formatCurrency(Math.abs(adj.price ?? 0), i18n.language, currency);
                const i18nKey = isDiscount ? 'promo.lineCallout' : 'promo.lineCalloutUpcharge';
                return (
                    <span
                        key={adj.priceAdjustmentId}
                        className={cn('text-xs font-medium', isDiscount ? 'text-promo-discount' : 'text-foreground')}>
                        {t(i18nKey, { label, amount })}
                    </span>
                );
            })}
        </div>
    );
}

/**
 * Resolves `promotionsPromise` via `use()` and passes the resulting map to
 * `CartLinePromoCallouts`. Must be rendered inside a `<Suspense>` boundary so
 * the line item row never blocks on the Promotions API response.
 *
 * When the promise is absent, the resolved `promotions` prop (if any) is used
 * so stories and unit tests can still supply a static map.
 */
export function DeferredCartLinePromoCallouts({
    promotionsPromise,
    ...props
}: ComponentProps<typeof CartLinePromoCallouts> & {
    promotionsPromise?: Promise<Record<string, ShopperPromotions.schemas['Promotion']>>;
}) {
    const resolved = promotionsPromise ? use(promotionsPromise) : undefined;
    return <CartLinePromoCallouts {...props} promotions={resolved ?? props.promotions} />;
}
